import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { Loader2, Download, RefreshCcw, MapPin, Calendar, Clock, User, Sparkles, PieChart } from 'lucide-react';
import { astrologyAPI } from '../../api/connectivityApis/astrology.api';

// 🟢 Import your cities array directly
import { cities, type City } from '../../utils/cities';

export default function ExpertKundli() {
  const [isGenerating, setIsGenerating] = useState(false);
  const [chartUrl, setChartUrl] = useState<string | null>(null);
  const [dashaData, setDashaData] = useState<any>(null);

  // 🟢 Tab State: 'kundli' | 'dasha'
  const [activeTab, setActiveTab] = useState<'kundli' | 'dasha'>('kundli');

  // Form State
  const [formData, setFormData] = useState({
    name: '',
    dateOfBirth: '',
    timeOfBirth: '',
    latitude: '',
    longitude: '',
    style: 'north' as 'north' | 'south' | 'east'
  });

  // 🟢 City Search State
  const [cityQuery, setCityQuery] = useState('');
  const [cityResults, setCityResults] = useState<City[]>([]);
  const [isCityOpen, setIsCityOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Handle click outside for city dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsCityOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Instant Local Filtering for Cities
  useEffect(() => {
    if (cityQuery.trim().length < 2) {
      setCityResults([]);
      return;
    }
    
    const filtered = cities.filter((c) => 
      c.city.toLowerCase().includes(cityQuery.toLowerCase()) || 
      c.state.toLowerCase().includes(cityQuery.toLowerCase())
    ); 

    const uniqueCities = Array.from(new Map(filtered.map(item => [item.city, item])).values()).slice(0, 8);
    setCityResults(uniqueCities);
  }, [cityQuery]);

  const handleSelectCity = (selectedCity: City) => {
    setCityQuery(`${selectedCity.city}, ${selectedCity.state}`);
    setIsCityOpen(false);
    
    setFormData(prev => ({
      ...prev,
      latitude: String(selectedCity.lat),
      longitude: String(selectedCity.lng)
    }));
  };

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.dateOfBirth || !formData.timeOfBirth || !formData.latitude || !formData.longitude) {
      toast.error("Please fill in Date of Birth, Time of Birth, and select a valid Place of Birth.");
      return;
    }

    setIsGenerating(true);
    setChartUrl(null);
    setDashaData(null);

    // Format YYYY-MM-DD from HTML date picker to DD/MM/YYYY for Dasha API
    let formattedDob = formData.dateOfBirth;
    if (formData.dateOfBirth.includes('-')) {
      const [yyyy, mm, dd] = formData.dateOfBirth.split('-');
      formattedDob = `${dd}/${mm}/${yyyy}`;
    }

    try {
      // 🟢 Parallel API Execution for Kundli & Dasha
      const [kundliRes, dashaRes] = await Promise.allSettled([
        astrologyAPI.generateManualKundli({
          dateOfBirth: formData.dateOfBirth,
          timeOfBirth: formData.timeOfBirth,
          latitude: formData.latitude,
          longitude: formData.longitude,
          style: formData.style
        }),
        astrologyAPI.fetchIndependentDasha({
          dateOfBirth: formattedDob,
          timeOfBirth: formData.timeOfBirth,
          latitude: formData.latitude,
          longitude: formData.longitude,
          timezone: 5.5
        })
      ]);

      let hasSuccess = false;

      if (kundliRes.status === 'fulfilled') {
        const finalUrl = kundliRes.value?.url || kundliRes.value?.chartUrl || kundliRes.value?.data?.url;
        if (finalUrl) {
          setChartUrl(finalUrl);
          hasSuccess = true;
        }
      } else {
        console.error("Failed to generate Kundli chart:", kundliRes.reason);
      }

      if (dashaRes.status === 'fulfilled' && dashaRes.value) {
        setDashaData(dashaRes.value);
        hasSuccess = true;
      } else if (dashaRes.status === 'rejected') {
        console.error("Failed to fetch Dasha report:", dashaRes.reason);
      }

      if (hasSuccess) {
        toast.success("Astrological analysis generated successfully!");
      } else {
        toast.error("Failed to generate analysis. Please check details and try again.");
      }
    } catch (error) {
      console.error("Failed to generate astrological reports:", error);
      toast.error("An unexpected error occurred. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleReset = () => {
    setFormData({
      name: '',
      dateOfBirth: '',
      timeOfBirth: '',
      latitude: '',
      longitude: '',
      style: 'north'
    });
    setCityQuery('');
    setChartUrl(null);
    setDashaData(null);
    setActiveTab('kundli');
  };

  const dashaOrder = dashaData?.order_of_dashas;

  // Helper function to render a full Dasha level table cleanly
  const renderDashaTable = (
    title: string, 
    dataArray: Array<{ name: string; start: string; end: string }>, 
    activePlanetName?: string
  ) => {
    if (!Array.isArray(dataArray) || dataArray.length === 0) return null;

    return (
      <div className="bg-slate-950/80 border border-slate-800 rounded-xl p-5 space-y-3">
        <h4 className="text-xs font-bold text-slate-300 uppercase tracking-wider flex items-center gap-2">
          <Calendar className="w-4 h-4 text-indigo-400" /> {title}
        </h4>
        
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs text-slate-300">
            <thead className="bg-slate-900 text-slate-400 uppercase text-[10px] tracking-wider">
              <tr>
                <th className="py-2.5 px-3 rounded-l-lg">Planet</th>
                <th className="py-2.5 px-3">Start Date</th>
                <th className="py-2.5 px-3 rounded-r-lg">End Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60">
              {dataArray.map((item: any, idx: number) => {
                const isCurrent = activePlanetName && item.name?.toLowerCase() === activePlanetName.toLowerCase();
                return (
                  <tr key={idx} className={isCurrent ? "bg-indigo-950/50 text-indigo-300 font-bold" : "hover:bg-slate-900/40"}>
                    <td className="py-2.5 px-3 flex items-center gap-2">
                      {isCurrent && <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-pulse"></span>}
                      {item.name}
                    </td>
                    <td className="py-2.5 px-3 text-slate-400">{item.start}</td>
                    <td className="py-2.5 px-3 text-slate-400">{item.end}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6 font-sans">
      
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Kundli & Dasha Generator Tool</h1>
          <p className="text-slate-500 text-sm mt-1">Instantly generate accurate birth charts and Vimshottari dasha timelines.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        
        {/* LEFT COLUMN: Input Form */}
        <div className="lg:col-span-5 bg-white border border-slate-200 rounded-2xl shadow-sm p-6">
          <form onSubmit={handleGenerate} className="space-y-5">
            
            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">Subject Name (Optional)</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                <input 
                  type="text" 
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="e.g. John Doe"
                  className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">Date of Birth *</label>
                <div className="relative cursor-pointer group">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-hover:text-indigo-500 transition-colors pointer-events-none z-10" />
                  <input 
                    type="date" 
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={(e) => {
                      handleInputChange(e);
                      if (e.target.value) e.target.blur();
                    }}
                    required
                    className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none transition-all cursor-pointer text-slate-700
                      [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:left-0"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">Time of Birth *</label>
                <div className="relative cursor-pointer group">
                  <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-hover:text-indigo-500 transition-colors pointer-events-none z-10" />
                  <input 
                    type="time" 
                    name="timeOfBirth"
                    value={formData.timeOfBirth}
                    onChange={(e) => {
                      handleInputChange(e);
                      if (e.target.value) e.target.blur();
                    }}
                    required
                    className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none transition-all cursor-pointer text-slate-700
                      [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:left-0"
                  />
                </div>
              </div>
            </div>

            {/* City Autocomplete */}
            <div className="relative" ref={dropdownRef}>
              <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">Place of Birth *</label>
              <div className="relative group">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-indigo-500 transition-colors pointer-events-none" />
                <input 
                  type="text" 
                  placeholder="Search city..." 
                  value={cityQuery} 
                  onChange={(e) => {
                    setCityQuery(e.target.value);
                    setIsCityOpen(true);
                    if (formData.latitude) {
                      setFormData(prev => ({ ...prev, latitude: '', longitude: '' }));
                    }
                  }}
                  required
                  className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none transition-all text-slate-700" 
                />
              </div>

              {isCityOpen && cityResults.length > 0 && (
                <ul className="absolute top-full mt-1 left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-xl z-50 max-h-48 overflow-y-auto divide-y divide-gray-50">
                  {cityResults.map((place, idx) => (
                    <li 
                      key={idx} 
                      onClick={() => handleSelectCity(place)}
                      className="px-4 py-2.5 hover:bg-indigo-50 cursor-pointer flex items-center gap-3 transition-colors"
                    >
                      <MapPin className="w-4 h-4 text-indigo-400 shrink-0" />
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-slate-900">{place.city}</span>
                        <span className="text-xs text-slate-500">{place.state}, India</span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">Chart Style</label>
              <select 
                name="style"
                value={formData.style}
                onChange={handleInputChange}
                className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none transition-all cursor-pointer text-slate-700"
              >
                <option value="north">North Indian</option>
                <option value="south">South Indian</option>
                <option value="east">East Indian</option>
              </select>
            </div>

            <div className="pt-4 flex gap-3">
              <button 
                type="button"
                onClick={handleReset}
                className="px-6 py-2.5 bg-slate-100 text-slate-600 font-semibold rounded-lg hover:bg-slate-200 transition-colors"
              >
                Reset
              </button>
              <button 
                type="submit"
                disabled={isGenerating}
                className="flex-1 bg-indigo-600 text-white font-bold py-2.5 rounded-lg shadow-md hover:bg-indigo-700 transition-all flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" /> Generating...
                  </>
                ) : (
                  <>
                    <RefreshCcw className="w-5 h-5" /> Generate Analysis
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* RIGHT COLUMN: Output Display with 2 Tabs */}
        <div className="lg:col-span-7 bg-slate-900 border border-slate-800 rounded-2xl shadow-xl flex flex-col overflow-hidden min-h-[550px]">
          
          {/* TAB HEADER BAR */}
          <div className="p-3 border-b border-slate-800 bg-slate-950 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2 bg-slate-900 p-1 rounded-xl border border-slate-800">
              <button
                type="button"
                onClick={() => setActiveTab('kundli')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition-all ${
                  activeTab === 'kundli'
                    ? 'bg-indigo-600 text-white shadow-md'
                    : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60'
                }`}
              >
                <PieChart className="w-4 h-4" />
                <span>Kundli Chart</span>
              </button>

              <button
                type="button"
                onClick={() => setActiveTab('dasha')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition-all ${
                  activeTab === 'dasha'
                    ? 'bg-indigo-600 text-white shadow-md'
                    : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60'
                }`}
              >
                <Sparkles className="w-4 h-4" />
                <span>Complete Dasha</span>
              </button>
            </div>

            {/* DOWNLOAD BUTTON FOR KUNDLI CHART */}
            {activeTab === 'kundli' && chartUrl && (
              <a 
                href={chartUrl} 
                download="kundli-chart.png" 
                target="_blank" 
                rel="noreferrer"
                className="text-xs font-bold bg-slate-800 text-slate-300 hover:text-white px-3 py-1.5 rounded flex items-center gap-1.5 transition-colors"
              >
                <Download className="w-3 h-3" /> Download
              </a>
            )}
          </div>

          {/* TAB CONTENT PANEL */}
          <div className="flex-1 flex flex-col p-6 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-slate-800 to-slate-900 overflow-y-auto max-h-[750px]">
            {isGenerating ? (
              <div className="flex-1 flex flex-col items-center justify-center text-indigo-400">
                <Loader2 className="w-12 h-12 animate-spin mb-4" />
                <p className="font-medium animate-pulse">Calculating planetary positions and Dasha cycles...</p>
              </div>
            ) : activeTab === 'kundli' ? (
              /* KUNDLI TAB CONTENT */
              <div className="flex-1 flex items-center justify-center">
                {chartUrl ? (
                  <img 
                    src={chartUrl} 
                    alt="Generated Kundli" 
                    className="max-w-full max-h-[500px] object-contain rounded bg-white p-2 shadow-2xl animate-in zoom-in duration-300"
                  />
                ) : (
                  <div className="text-center text-slate-500 max-w-sm">
                    <div className="w-20 h-20 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
                      <PieChart className="w-8 h-8 opacity-50" />
                    </div>
                    <p>Enter birth details and click generate to view the astrological birth chart here.</p>
                  </div>
                )}
              </div>
            ) : (
              /* DASHA TAB CONTENT */
              <div className="flex-1 space-y-6">
                {dashaData ? (
                  <div className="space-y-6 animate-in fade-in duration-300">
                    
                    {/* CURRENT DASHA HIERARCHY CARD */}
                    {dashaOrder && (
                      <div className="bg-slate-950/80 border border-slate-800 rounded-xl p-5 space-y-3">
                        <h4 className="text-xs font-bold text-indigo-400 uppercase tracking-wider flex items-center gap-2 border-b border-slate-800 pb-2">
                          <Clock className="w-4 h-4 text-indigo-400" /> Current Active Dasha Sequence
                        </h4>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                          {dashaOrder.major && (
                            <div className="bg-slate-900/90 border border-slate-800 p-3 rounded-lg flex justify-between items-center">
                              <span className="text-slate-400 font-semibold">Mahadasha</span>
                              <div className="text-right">
                                <span className="font-bold text-indigo-400 text-sm block">{dashaOrder.major.name}</span>
                                <span className="text-[10px] text-slate-500">{dashaOrder.major.start} - {dashaOrder.major.end}</span>
                              </div>
                            </div>
                          )}

                          {dashaOrder.minor && (
                            <div className="bg-slate-900/90 border border-slate-800 p-3 rounded-lg flex justify-between items-center">
                              <span className="text-slate-400 font-semibold">Antardasha</span>
                              <div className="text-right">
                                <span className="font-bold text-indigo-400 text-sm block">{dashaOrder.minor.name}</span>
                                <span className="text-[10px] text-slate-500">{dashaOrder.minor.start} - {dashaOrder.minor.end}</span>
                              </div>
                            </div>
                          )}

                          {dashaOrder.sub_minor && (
                            <div className="bg-slate-900/90 border border-slate-800 p-3 rounded-lg flex justify-between items-center">
                              <span className="text-slate-400 font-semibold">Pratyantar</span>
                              <div className="text-right">
                                <span className="font-bold text-indigo-400 text-sm block">{dashaOrder.sub_minor.name}</span>
                                <span className="text-[10px] text-slate-500">{dashaOrder.sub_minor.start} - {dashaOrder.sub_minor.end}</span>
                              </div>
                            </div>
                          )}

                          {dashaOrder.sub_sub_minor && (
                            <div className="bg-slate-900/90 border border-slate-800 p-3 rounded-lg flex justify-between items-center">
                              <span className="text-slate-400 font-semibold">Sookshma</span>
                              <div className="text-right">
                                <span className="font-bold text-indigo-400 text-sm block">{dashaOrder.sub_sub_minor.name}</span>
                                <span className="text-[10px] text-slate-500">{dashaOrder.sub_sub_minor.start} - {dashaOrder.sub_sub_minor.end}</span>
                              </div>
                            </div>
                          )}

                          {dashaOrder.sub_sub_sub_minor && (
                            <div className="bg-slate-900/90 border border-slate-800 p-3 rounded-lg flex justify-between items-center col-span-1 sm:col-span-2">
                              <span className="text-slate-400 font-semibold">Prana Dasha</span>
                              <div className="text-right">
                                <span className="font-bold text-indigo-400 text-sm block">{dashaOrder.sub_sub_sub_minor.name}</span>
                                <span className="text-[10px] text-slate-500">{dashaOrder.sub_sub_sub_minor.start} - {dashaOrder.sub_sub_sub_minor.end}</span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* 🟢 FULL DASHA TIMELINE TABLES FOR ALL 5 LEVELS */}
                    {renderDashaTable('1. Full Mahadasha Timeline', dashaData.mahadasha, dashaOrder?.major?.name)}
                    {renderDashaTable('2. Full Antardasha Timeline', dashaData.antardasha, dashaOrder?.minor?.name)}
                    {renderDashaTable('3. Full Pratyantardasha (Paryantardasha)', dashaData.paryantardasha || dashaData.pratyantardasha, dashaOrder?.sub_minor?.name)}
                    {renderDashaTable('4. Full Sookshma Dasha Timeline', dashaData.Shookshamadasha || dashaData.sookshmadasha || dashaData.shookshamadasha, dashaOrder?.sub_sub_minor?.name)}
                    {renderDashaTable('5. Full Prana Dasha Timeline', dashaData.Pranadasha || dashaData.pranadasha, dashaOrder?.sub_sub_sub_minor?.name)}

                  </div>
                ) : (
                  <div className="flex-1 flex flex-col items-center justify-center text-center text-slate-500 py-12">
                    <div className="w-20 h-20 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Sparkles className="w-8 h-8 opacity-50" />
                    </div>
                    <p className="max-w-sm">Enter birth details and click generate to view the complete Vimshottari Dasha timeline here.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}